import React, { useEffect, useState } from "react";
import { Alignment, Fit, Layout, useRive } from "@rive-app/react-canvas";
import "../styles/pages/StoryLand.scss";
import CategoryList from "../components/utils/CategoryList";
import BookCoverList from "../components/utils/BookCoverList";
import {
  getStoryLandList,
  getStoryLandListByGyeongnam,
} from "../api/LibraryAPI";
import AllViewModal from "../components/utils/modal/AllViewModal";
import { useNavigate } from "react-router-dom";
import Wrapper from "../components/utils/Wrapper";
import SeriesBookList from "../components/utils/SeriesBookList";
import PaginationButtons from "../components/utils/button/PaginationButtons";
import ListLoading from "../components/utils/loading/ListLoading";
import useAppTranslation from "../hooks/useAppTranslation";
import { getLocalizedAssetPath } from "../utils/getLocalizedLogo";

const StoryLandCharacterRive = ({ src }) => {
  const { RiveComponent } = useRive(
    {
      src,
      autoplay: true,
      layout: new Layout({
        fit: Fit.Contain,
        alignment: Alignment.Center,
      }),
    },
    {
      fitCanvasToArtboardHeight: true,
    }
  );

  return <RiveComponent />;
};

const StoryLand = () => {
  const { t, language } = useAppTranslation("pages/storyLand");
  const navigate = useNavigate();
  const [bookList, setBookList] = useState([]);
  const [displayedBooks, setDisplayedBooks] = useState([]);
  const [booksPerPage, setBooksPerPage] = useState(8);
  const [selectedCategory, setSelectedCategory] = useState("all");
  const [selectedBook, setSelectedBook] = useState(null);
  const [currentPage, setCurrentPage] = useState(1);
  const [loading, setLoading] = useState(false);
  const [showAllModal, setShowAllModal] = useState(false);
  const categories = [
    { key: "all", label: t("categories.all") },
    { key: "single", label: t("categories.single") },
    { key: "series", label: t("categories.series") },
  ];
  const USER_TYPE = localStorage.getItem("userType");
  const ORGANIZATION = localStorage.getItem("organization");

  // const handleBookSelect = (book) => {
  //     setSelectedBook(book);
  //     // 선택 후 모달 닫기 또는 원하는 추가 작업 실행
  //     setShowAllModal(false);
  //     navigate('/reading', {
  //         state: {
  //             bookList: book[i],
  //             pageName: pageName,
  //             bookID: book_id,
  //         },
  //     });
  // };

  useEffect(() => {
    if (showAllModal) {
      document.body.style.overflow = "hidden";
    } else {
      document.body.style.overflow = "";
    }
    return () => {
      document.body.style.overflow = "";
    };
  }, [showAllModal]);

  useEffect(() => {
    async function fetchData() {
      try {
        let res;

        if (ORGANIZATION === "gyeongnam") {
          res = await getStoryLandListByGyeongnam();
        } else {
          res = await getStoryLandList();
        }

        if (res.success) {
          setBookList(res.data);
        } else {
          console.error("API 에러:", res.error.errMsg);
        }
      } catch (error) {
        console.error("API 호출 중 에러 발생:", error);
      }
    }
    fetchData();
  }, []);

  useEffect(() => {
    let filteredBooks = [];
    switch (selectedCategory) {
      case "all":
        filteredBooks = bookList;
        break;
      case "single":
        filteredBooks = bookList.filter((book) => !book.is_series);
        break;
      case "series":
        filteredBooks = bookList.filter((book) => book.is_series);
        break;
      default:
        filteredBooks = bookList;
    }
    setDisplayedBooks(filteredBooks);
    setCurrentPage(1);
  }, [selectedCategory, bookList]);

  useEffect(() => {
    const updateBooksPerPage = () => {
      if (window.innerWidth < 1025) {
        setBooksPerPage(4);
      } else if (window.innerWidth < 1440) {
        setBooksPerPage(6);
      } else {
        setBooksPerPage(8);
      }
    };

    updateBooksPerPage();
    window.addEventListener("resize", updateBooksPerPage);
    return () => {
      window.removeEventListener("resize", updateBooksPerPage);
    };
  }, []);

  const totalPages = Math.ceil(displayedBooks.length / booksPerPage);

  useEffect(() => {
    console.log("현재 페이지:", currentPage, "총 페이지:", totalPages);
    if (totalPages === 0) {
      setCurrentPage(1);
      return;
    }

    if (currentPage > totalPages) {
      setCurrentPage(totalPages);
    }
  }, [currentPage, totalPages]);

  const handlePrevPage = () => {
    if (currentPage <= 1) return;
    setLoading(true);
    setCurrentPage((p) => Math.max(1, p - 1));
  };

  const handleNextPage = () => {
    if (totalPages === 0 || currentPage >= totalPages) return;
    setLoading(true);
    setCurrentPage((p) => Math.min(totalPages, p + 1));
  };

  const handleClose = () => {
    setShowAllModal(false);
  };

  // 테스트중
  const getBookId = (book) => book?.book_id ?? book?.id ?? book?.bookId ?? null;

  const handleBookSelect = (book) => {
    const bookId = getBookId(book);
    if (!bookId) {
      console.warn("[StoryLand] 책 ID를 찾을 수 없어요:", book);
      return;
    }
    localStorage.setItem("book_id", String(bookId));
    navigate("/reading", {
      state: {
        pageName: "storyland",
        bookID: bookId,
      },
    });
    // console.log('선택된 책:', book);
    // localStorage.setItem('book_id', book.book_id);
    // navigate('/reading', {
    //   state: {
    //     pageName: 'storyland',
    //     bookID: book.book_id,
    //   },
    // });
  };

  return (
    <Wrapper>
      <section
        className={
          ORGANIZATION === "gyeongnam"
            ? "storyland-section gyeongnam"
            : "storyland-section"
        }
      >
        <div className="inner">
          <button
            type="button"
            className="all-view-btn"
            onClick={() => {
              setShowAllModal(true);
            }}
          >
            <span>{t("allView")} +</span>
          </button>
          <h3 className="storyland-tit">
            <figure>
              <img
                src={getLocalizedAssetPath("/images/storyland/storyland-tit.svg")}
                alt=""
                loading="lazy"
                decoding="async"
              />
            </figure>
          </h3>
          <div className="storyland-container">
            {ORGANIZATION === "gyeongnam" ? null : (
              <div className="str-box left">
                <CategoryList
                  categories={categories}
                  selectedCategory={selectedCategory}
                  onSelectCategory={setSelectedCategory}
                />
              </div>
            )}

            <div
              className={`str-box right ${
                selectedCategory === "series" ? "series" : ""
              }`}
            >
              <div className="box-title">
                <p>{t("summary", { count: displayedBooks.length })}</p>
                <select>
                  <option>{t("sort.recommended")}</option>
                </select>
              </div>
              {selectedCategory === "series" ? (
                <SeriesBookList
                  books={displayedBooks}
                  onStorySelect={handleBookSelect}
                />
              ) : (
                <>
                  <BookCoverList
                    books={displayedBooks}
                    currentPage={currentPage}
                    booksPerPage={booksPerPage}
                    pageName="storyland"
                    onStorySelect={handleBookSelect}
                    onAllImagesLoaded={() => setLoading(false)}
                  />
                  <PaginationButtons
                    onPrev={handlePrevPage}
                    onNext={handleNextPage}
                    currentPage={currentPage}
                    totalPages={totalPages}
                  />
                  {loading && <ListLoading />}
                </>
              )}
            </div>
          </div>
        </div>
        <figure
          className={
            ORGANIZATION === "gyeongnam"
              ? "navigate-char gyeongnam"
              : "navigate-char"
          }
          aria-hidden="true"
        >
          <StoryLandCharacterRive
            key={language}
            src={getLocalizedAssetPath("/riv/storyLand/storyland-arial.riv")}
          />
        </figure>
      </section>
      {showAllModal && (
        <AllViewModal
          bookList={bookList}
          onClose={handleClose}
          onStorySelect={handleBookSelect}
        />
      )}
    </Wrapper>
  );
};

export default StoryLand;
